import { Box, Text } from '@user-portal/wikr-components';
import { cancelFlow_subscriptionScreenLoad } from 'analytics/trackers/cancelFlow';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { selectIsUserFromCalifornia } from 'store/user/selectors';

import { useSelector, useSubscriptions } from 'hooks';
import {
    useCaliforniaFlow,
    useCancelSubscription,
    useCommonSubscriptionLogic,
    useDiscountProduct,
    useRestoreSubscription,
    useResumeSubscription,
} from 'hooks/subscriptions';
import { useSubscriptionsCount } from 'hooks/subscriptions/useSubscriptionsCount';

import { isMainSubscription } from 'helpers/subscriptions';

import { ISubscription } from 'types/subscription';

import SubscriptionCard from './components/SubscriptionCard';
import SubscriptionCardAdditionalNote from './components/SubscriptionCard/components/subComponents/SubscriptionCardAdditionalNote';
import SubscriptionHelpButton from './components/SubscriptionHelpButton';
import SubscriptionsSkeleton from './components/SubscriptionsSkeleton';
import { CancellationStrategy, FlowType } from './types';

import * as S from './styled';

const SubscriptionsTab = () => {
    const { t } = useTranslation();

    const isFlowForCalifornia: boolean = useSelector(selectIsUserFromCalifornia);

    const { isLoading, subscriptions } = useSubscriptions();
    const { initRestoreSubscription } = useRestoreSubscription();
    const { initResumeSubscription } = useResumeSubscription();
    const { initSubscriptionCancellation, initSubscriptionPause } = useCancelSubscription(subscriptions);
    const { displayReturnOffer } = useCommonSubscriptionLogic(subscriptions);
    const { initCancellationForCalifornia } = useCaliforniaFlow(subscriptions);
    const { activeCount, canceledCount } = useSubscriptionsCount(subscriptions);

    useEffect(() => {
        if (subscriptions?.length) {
            cancelFlow_subscriptionScreenLoad({
                reminder: subscriptions.some((i) => i.reminder),
                free_month: subscriptions.some((i) => i.discounts.free_month),
                subscriptions_status: subscriptions.map((i) => ({ [i.external_id]: i.state })),
            });
        }
    }, [subscriptions]);

    useDiscountProduct(subscriptions);

    const sortedSubscriptions = subscriptions.sort((a: ISubscription) => {
        if (isMainSubscription(a.product_type)) {
            return -1;
        }

        return 0;
    });

    const cancellationStrategies: Record<string, CancellationStrategy> = {
        california: initCancellationForCalifornia,
        regular: initSubscriptionCancellation,
    };

    const flowConditions: Record<FlowType, boolean> = {
        california: isFlowForCalifornia,
        regular: !isFlowForCalifornia, // Regular flow is the default
    };

    const flowType = (Object.keys(flowConditions) as FlowType[]).find((key) => flowConditions[key]) || 'regular';

    const handleCancelFlow = cancellationStrategies[flowType];

    const displaySkeleton = isLoading && !sortedSubscriptions?.length;

    return (
        <S.Container $paddingTop={40} $paddingBottom={40} data-locator="subscriptionsTabContainer">
            {displaySkeleton ? (
                <SubscriptionsSkeleton data-locator="subscriptionsSkeleton" />
            ) : (
                <Box data-locator="subscriptionsContent">
                    {Boolean(activeCount) && (
                        <Text type="h6" mb={20} data-locator="activeSubscriptionsCount">
                            {t('subscription.text.activeCount', { count: activeCount })}
                        </Text>
                    )}
                    {Boolean(canceledCount) && !activeCount && (
                        <Text type="h6" mb={20} data-locator="canceledSubscriptionsCount">
                            {t('subscription.text.canceledCount', { count: canceledCount })}
                        </Text>
                    )}
                    <div data-locator="subscriptionsContainer">
                        {sortedSubscriptions.map((subscription: ISubscription, index) => (
                            <div key={subscription.id} data-locator={`subscription${index}`}>
                                {!isMainSubscription(subscription.product_type) && (
                                    <SubscriptionCardAdditionalNote
                                        data-locator={`subscriptionAdditionalNote${index}`}
                                    />
                                )}
                                <SubscriptionCard
                                    subscription={subscription}
                                    onCancelSubscription={handleCancelFlow}
                                    onReturnOffer={displayReturnOffer}
                                    onPauseSubscription={initSubscriptionPause}
                                    onResumeSubscription={initResumeSubscription}
                                    onRestoreSubscription={initRestoreSubscription}
                                    data-locator={`subscriptionCard${index}`}
                                />
                            </div>
                        ))}
                    </div>
                </Box>
            )}
            <SubscriptionHelpButton data-locator="subscriptionHelpButton" />
        </S.Container>
    );
};

export default SubscriptionsTab;
